// First-run onboarding for a freshly-installed store.
//
// A short, purpose-led wizard rather than one screen of six credential forms:
// a value welcome → Meta → GA4 (the two that move the needle most) → an
// optional "anything else?" step → a done screen, with a progress bar across
// the top. Each step leads with WHY it matters before asking for credentials,
// and connecting is encouraged but never required (Skip advances; the app gate
// still needs >= 1 destination before leaving onboarding). Anything saved is
// editable later from the Destinations hub.
//
// Design choices:
//   - Bite-sized + purpose-first over a wall of forms: most merchants start
//     with Meta or GA4 and add the rest later.
//   - Test and Save are separate — save creds without testing (see the outcome
//     on the dashboard) or test first to validate. Lowers commitment friction.
//   - Errors are per-card; a 4xx from Meta doesn't affect the GA4 form.
//   - The same *DestinationCard components back every step and the dashboard's
//     settings modal, so the save/test logic + muscle memory stay in one place.

import { useState, type ReactNode } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Collapsible,
  TextField,
  Banner,
  Badge,
  Link,
} from "@shopify/polaris";
import { apiFetch, ApiError } from "./api";
import { DESTINATION_DOCS } from "./help-links";
import { replayNote, withReplayNote } from "./replay-note";
import {
  DESTINATION_CATALOG,
  DESTINATION_TYPES,
  type DestinationType,
} from "./destination-catalog";
// CIRCULAR, and safe: destination-cards/index.ts imports the six card
// components from THIS module. That resolves because every card is an
// `export function`, and ESM hoists function declarations before a module
// body runs, so the registry sees real functions even when this module is
// loaded first. It would NOT be safe if the cards became `const Card = () =>`
// arrow bindings, which are in temporal dead zone during the cycle and would
// register as undefined -- rendering nothing, with no error.
// destination-cards/registry.test.ts asserts every entry is a function,
// which is the tripwire for exactly that change.
import { DESTINATION_CARDS } from "./destination-cards";
import { OVERLAP_COPY } from "./overlap-copy";
import type { DestinationOverlap } from "./overlap-types";

// Per-store hints the backend resolves once at /api/destinations time and
// the destination cards use to render overlap warnings. See
// src/lib/installed-apps.ts and DECISIONS.md §ADR-016.
export interface StoreContext {
  /**
   * LEGACY. The flat list of destination types whose own Shopify app was
   * seen. Kept on the wire for cache rows written before 2026-10-18.
   * NOTHING READS IT: a destination type on its own cannot carry which app
   * was found or what is honest to say about it, which is what the old
   * banner got wrong. Read `overlaps` instead.
   */
  overlappingDestinations: string[];
  /**
   * One entry per destination whose own Shopify app was seen, carrying the
   * static copy key for what we can honestly say about that app. Empty when
   * none were detected, when the probe could not run, or when this store's
   * cache row predates the field.
   */
  overlaps?: DestinationOverlap[];
  /**
   * Full-stack server-side tracking competitors detected (e.g.
   * ["Littledata"]). These forward to ALL the same destinations Datacrow
   * does, so running both double-counts everywhere — they're substitutes.
   * Empty when none detected or the probe couldn't run.
   */
  competitors: string[];
}

// The overlap banner's own name map is gone: it branched on a flat list of
// destination types and interpolated a name into a firm sentence. Overlap
// copy is now static per (app, destination) key in overlap-copy.ts, so
// nothing here interpolates a destination name. See DECISIONS.md ADR-016.

// Overlap note for a single destination: that destination's own official
// Shopify app was found on the storefront. Informational (no Save block),
// rendered above the form in the hub modal and in onboarding.
//
// Reads `overlaps`, NOT the flat `overlappingDestinations`. That is the whole
// point of the field: finding an app's pixel proves it is INSTALLED, and for
// most of these apps whether it ALSO reports from a server is held in
// settings we cannot read. The old banner branched on the flat list and so
// had nothing to say except a firm double-count claim it could not support.
// Each copy key names what we saw, says what we cannot see, and points at
// the merchant's own ad account. See src/lib/installed-apps.ts.
//
// Returns null when no overlap was detected, when the probe could not run,
// or when the key is one this frontend does not have copy for (a backend
// deployed ahead of the frontend). Silence is the right default: a banner we
// cannot word is worse than no banner.
export function DestinationOverlapWarning({
  destinationType,
  storeContext,
}: {
  destinationType: string;
  storeContext?: StoreContext;
}) {
  const hit = storeContext?.overlaps?.find((o) => o.destination === destinationType);
  if (!hit) return null;
  const copy = OVERLAP_COPY[hit.copyKey];
  if (!copy) return null;
  return (
    <Banner tone={copy.tone} title={copy.title}>
      <BlockStack gap="200">
        {copy.paragraphs.map((text, i) => (
          <p key={i}>{text}</p>
        ))}
      </BlockStack>
    </Banner>
  );
}

// Global warning when a full-stack server-side tracking competitor
// (Littledata, Elevar, ...) is detected. tone="warning" stays, unlike the
// per-destination notes above: this detection has real evidence behind it
// (Littledata is script-verified posting to its own collector, Elevar's
// config self-names its GTM host), so the tool IS relaying. What we cannot
// see is which destinations the merchant switched on inside it, so the
// doubling claim is scoped to wherever the two overlap rather than asserted
// everywhere. Returns null when none detected.
export function CompetitorOverlapBanner({
  storeContext,
}: {
  storeContext?: StoreContext;
}) {
  const competitors = storeContext?.competitors ?? [];
  if (competitors.length === 0) return null;
  const list = competitors.join(" and ");
  return (
    <Banner tone="warning" title={`${list} is also installed on this store`}>
      <BlockStack gap="200">
        <p>
          {list} forwards conversions to the same ad platforms Datacrow does,
          and we found its tracking on your storefront. What we cannot see is
          which destinations you have switched on inside it.
        </p>
        <p>
          Wherever both tools send to the same destination, that platform
          receives each order <strong>twice</strong> and cannot merge the two,
          because the event IDs do not match. Open {list} and note what it is
          connected to.
        </p>
        <p>
          Then pick one owner per destination. Keep {list} for the ones it
          already handles and leave those off here, or move them over and
          switch them off there. One destination fed by both tools is the case
          that breaks.
        </p>
      </BlockStack>
    </Banner>
  );
}

interface OnboardingProps {
  shop: string;
  // Called after a destination has been saved — parent re-fetches
  // /api/destinations to decide whether to transition to the dashboard.
  onFinished: () => void;
  // Per-store hints for conditional UX in the destination cards.
  // Optional so existing callers that haven't been updated yet still
  // type-check; cards treat `undefined` as `null` (no signal).
  storeContext?: StoreContext;
}

// Per-card save/test state. "idle" is the default; we never block one card
// on another's state.
export type CardState =
  | { kind: "idle" }
  | { kind: "working"; what: "test" | "save" }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

// Public view shape exposed by GET /api/destinations. Mirrored here so the
// cards can render "already configured" state when reused on the Settings
// → Destinations page. The dashboard's copy of this type is the same shape.
/**
 * The destinations behind the onboarding wizard's "anything else?" step, in
 * catalog order. Derived, so a new `more` destination appears here the moment
 * it has a catalog entry and a card.
 */
const MORE_TYPES: DestinationType[] = DESTINATION_TYPES.filter(
  (t) =>
    DESTINATION_CATALOG[t].group === "more" &&
    // Release-gated, not just grouped. Filtering on `group` alone offered
    // every hidden destination's card to every merchant in the wizard, which
    // defeats the gate on the one screen a new merchant definitely sees. A
    // preview shop configures a hidden destination from the Destinations hub,
    // which reads the per-shop availableTypes from the API; onboarding runs
    // once and does not need to.
    DESTINATION_CATALOG[t].release !== "hidden",
);

export interface DestinationCardExistingView {
  // DestinationType, not a hand-written union. A copy that stayed at six
  // while the API returned a seventh would not fail to compile: the type maps
  // keyed off it return undefined for the new one, and the merchant sees
  // "undefined is in test mode".
  type: DestinationType;
  enabled: boolean;
  configured: boolean;
  // This (web) service's dry-run flag for the type — true = deliveries are
  // logged, not sent. Drives the dry-run warning banner.
  dryRun: boolean;
  lastSuccessAt: string | null;
  // Per-field, display-safe hints about what's currently stored.
  // Empty / missing values mean we have no hint to show for that field.
  // Generated server-side by src/lib/destination-hints.ts; see that
  // file for the per-field redaction policy (tails of public IDs, "set"
  // for secrets, full passthrough for explicitly-non-secret values).
  hints?: Record<string, string>;
}

const STEPS = ["welcome", "meta", "ga4", "more", "done"] as const;

export function Onboarding({ shop, onFinished, storeContext }: OnboardingProps) {
  const shopify = useAppBridge();
  const [stepIndex, setStepIndex] = useState(0);
  // Which destination types were saved this run. Drives the per-step
  // Continue-vs-Skip label and the done screen. (App refetches /api/destinations
  // only on finish, so the wizard tracks its own progress locally.)
  const [savedTypes, setSavedTypes] = useState<Set<string>>(() => new Set());
  const savedAny = savedTypes.size > 0;
  const markSaved = (type: string) =>
    setSavedTypes((prev) => new Set(prev).add(type));

  const step = STEPS[stepIndex];
  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  return (
    <Page title="Set up Datacrow" subtitle={shop}>
      <BlockStack gap="500">
        <StepProgress current={stepIndex} total={STEPS.length} />

        {step === "welcome" && (
          <WelcomeStep storeContext={storeContext} onStart={goNext} />
        )}

        {step === "meta" && (
          <ConnectStep
            eyebrow="Recommended"
            title="Connect Meta first"
            purpose="Meta is where most stores see the biggest lift. Server-side events feed its targeting and let it match more of your shoppers, so your ads reach the right people and get credit for every sale."
            docsType="meta_capi"
            saved={savedTypes.has("meta_capi")}
            onBack={goBack}
            onNext={goNext}
          >
            <DestinationOverlapWarning
              destinationType="meta_capi"
              storeContext={storeContext}
            />
            <MetaCapiDestinationCard
              shopify={shopify}
              onSaved={() => markSaved("meta_capi")}
            />
          </ConnectStep>
        )}

        {step === "ga4" && (
          <ConnectStep
            eyebrow="Recommended"
            title="Add Google Analytics 4"
            purpose="Send your conversions to GA4 as well, so your analytics see the full picture, including the sales that never reach the browser pixel."
            docsType="ga4"
            saved={savedTypes.has("ga4")}
            onBack={goBack}
            onNext={goNext}
          >
            <DestinationOverlapWarning
              destinationType="ga4"
              storeContext={storeContext}
            />
            <Ga4DestinationCard
              shopify={shopify}
              onSaved={() => markSaved("ga4")}
            />
          </ConnectStep>
        )}

        {step === "more" && (
          <MoreStep
            shopify={shopify}
            storeContext={storeContext}
            anySaved={savedAny}
            onSaved={markSaved}
            onBack={goBack}
            onNext={goNext}
          />
        )}

        {step === "done" && (
          <DoneStep
            savedCount={savedTypes.size}
            onFinish={onFinished}
            onConnect={() => setStepIndex(1)}
          />
        )}
      </BlockStack>
    </Page>
  );
}

// Thin segmented progress bar across the top of the wizard. Brand green marks
// the current + completed steps. Hand-rolled (Polaris has no stepper) to match
// the dashboard's div-based bars.
function StepProgress({ current, total }: { current: number; total: number }) {
  return (
    <div
      style={{ display: "flex", gap: 6 }}
      role="img"
      aria-label={`Step ${current + 1} of ${total}`}
    >
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            background: i <= current ? "#2b9b83" : "#e3e3e3",
          }}
        />
      ))}
    </div>
  );
}

// Step 1: value-led welcome. Leads with what the merchant gets before asking
// for anything.
function WelcomeStep({
  storeContext,
  onStart,
}: {
  storeContext?: StoreContext;
  onStart: () => void;
}) {
  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="200">
            <Text as="h2" variant="headingLg">
              Let's get your store's data flowing
            </Text>
            <Text as="p" tone="subdued">
              Datacrow sends every sale and signal from your store straight to
              your ad and analytics platforms, from our servers. There's no
              browser pixel to block.
            </Text>
          </BlockStack>
          <BlockStack gap="200">
            <CheckLine>
              <strong>Reach the right people.</strong> Ad platforms can match
              more of your shoppers to real accounts.
            </CheckLine>
            <CheckLine>
              <strong>Get credit for every sale.</strong> Recover the
              conversions a browser pixel loses to ad blockers and iOS.
            </CheckLine>
            <CheckLine>
              <strong>Spend smarter.</strong> Cleaner signal means better
              targeting and a better return on ad spend.
            </CheckLine>
          </BlockStack>
          <InlineStack>
            <Button variant="primary" size="large" onClick={onStart}>
              Get started
            </Button>
          </InlineStack>
          <Text as="p" tone="subdued" variant="bodySm">
            Takes a couple of minutes. You can change anything later.
          </Text>
        </BlockStack>
      </Card>
      <CompetitorOverlapBanner storeContext={storeContext} />
    </BlockStack>
  );
}

// A connect step: purpose copy on top, the destination card (passed as
// children) in the middle, Back / Skip-or-Continue nav at the bottom. The
// forward button reads "Continue" once this step's destination is saved, and
// "Skip for now" before that, so connecting is encouraged but never required.
function ConnectStep({
  eyebrow,
  title,
  purpose,
  docsType,
  saved,
  onBack,
  onNext,
  children,
}: {
  eyebrow: string;
  title: string;
  purpose: string;
  docsType: string;
  saved: boolean;
  onBack: () => void;
  onNext: () => void;
  children: ReactNode;
}) {
  return (
    <BlockStack gap="400">
      <BlockStack gap="150">
        <Text as="p" tone="subdued" variant="bodySm">
          {eyebrow}
        </Text>
        <Text as="h2" variant="headingLg">
          {title}
        </Text>
        <Text as="p" tone="subdued">
          {purpose}
        </Text>
        <DocsLink type={docsType} />
      </BlockStack>
      {children}
      <InlineStack align="space-between" blockAlign="center">
        <Button variant="tertiary" onClick={onBack}>
          Back
        </Button>
        {saved ? (
          <Button variant="primary" onClick={onNext}>
            Continue
          </Button>
        ) : (
          <Button variant="tertiary" onClick={onNext}>
            Skip for now
          </Button>
        )}
      </InlineStack>
    </BlockStack>
  );
}

// Optional step: the remaining four platforms, each behind a disclosure so the
// step stays bite-sized. Expand only the ones you advertise on.
function MoreStep({
  shopify,
  storeContext,
  anySaved,
  onSaved,
  onBack,
  onNext,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  storeContext?: StoreContext;
  anySaved: boolean;
  onSaved: (type: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <BlockStack gap="400">
      <BlockStack gap="150">
        <Text as="p" tone="subdued" variant="bodySm">
          Optional
        </Text>
        <Text as="h2" variant="headingLg">
          Advertise anywhere else?
        </Text>
        <Text as="p" tone="subdued">
          Add any of these now, or anytime from your Destinations page. Expand
          the ones you use.
        </Text>
      </BlockStack>

      {/*
        Iterated over the catalog's `more` group, not hardcoded. Four literal
        blocks meant a new more-group destination could have a catalog entry,
        a card, a release gate, a dry-run flag and every derived map, and
        still never appear in onboarding -- offering the merchant fewer
        destinations here than the hub does, with nothing failing.
      */}
      {MORE_TYPES.map((type) => {
        const Card = DESTINATION_CARDS[type];
        return (
          <CollapsibleSection key={type} idKey={type}>
            <DestinationOverlapWarning destinationType={type} storeContext={storeContext} />
            <Card shopify={shopify} onSaved={() => onSaved(type)} />
          </CollapsibleSection>
        );
      })}

      <InlineStack align="space-between" blockAlign="center">
        <Button variant="tertiary" onClick={onBack}>
          Back
        </Button>
        <Button variant="primary" onClick={onNext}>
          {anySaved ? "Continue" : "Skip for now"}
        </Button>
      </InlineStack>
    </BlockStack>
  );
}

// A single collapsible platform row inside the optional step. No Card wrapper —
// the destination card it reveals brings its own — so we don't nest cards.
// The label is built from the catalog as "<name>: <pitch>", not passed in:
// all four of these sections are destinations, and a literal label here was a
// fifth copy of a destination's name. A `more` destination must carry a
// pitch, which src/types/destination.test.ts asserts, so this cannot render
// a bare "Klaviyo:".
function CollapsibleSection({
  idKey,
  children,
}: {
  idKey: DestinationType;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const entry = DESTINATION_CATALOG[idKey];
  const label = `${entry.name}: ${entry.pitch}`;
  return (
    <BlockStack gap="200">
      <Button
        variant="plain"
        textAlign="left"
        fullWidth
        disclosure={open ? "up" : "down"}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </Button>
      <Collapsible id={`onb-more-${idKey}`} open={open}>
        <BlockStack gap="300">
          <DocsLink type={idKey} />
          {children}
        </BlockStack>
      </Collapsible>
    </BlockStack>
  );
}

// Final step. Celebrates a connected store, or nudges back to connect if the
// merchant skipped everything (the app gate also requires >= 1 destination).
function DoneStep({
  savedCount,
  onFinish,
  onConnect,
}: {
  savedCount: number;
  onFinish: () => void;
  onConnect: () => void;
}) {
  if (savedCount === 0) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingLg">
            Connect one platform to finish
          </Text>
          <Text as="p" tone="subdued">
            You skipped every platform, so nothing is tracking yet. Connect one
            (Meta is a good place to start) and you're live.
          </Text>
          <InlineStack>
            <Button variant="primary" onClick={onConnect}>
              Connect a platform
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
    );
  }
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingLg">
          You're all set
        </Text>
        <Text as="p" tone="subdued">
          {savedCount === 1
            ? "Your first platform is connected"
            : `${savedCount} platforms are connected`}{" "}
          and your events are flowing. Head to your dashboard to watch them land,
          and add more destinations anytime.
        </Text>
        <InlineStack>
          <Button variant="primary" size="large" onClick={onFinish}>
            Go to dashboard
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// "Read the setup guide" link into the public help docs for a destination. The
// card's own description links to the platform (where to grab credentials);
// this links to our guide (the full how + why).
function DocsLink({ type }: { type: string }) {
  const url = DESTINATION_DOCS[type];
  if (!url) return null;
  return (
    <InlineStack gap="100" blockAlign="center">
      <Text as="span" tone="subdued" variant="bodySm">
        New to this one?
      </Text>
      <Link url={url} external>
        Read the setup guide
      </Link>
    </InlineStack>
  );
}

// Small green check + text line for the welcome benefits.
function CheckLine({ children }: { children: ReactNode }) {
  return (
    <InlineStack gap="200" blockAlign="start" wrap={false}>
      <Text as="span" tone="success" fontWeight="bold">
        ✓
      </Text>
      <Text as="span" tone="subdued">
        {children}
      </Text>
    </InlineStack>
  );
}

// ---------- Draft persistence (in-memory; survives modal close) ----------
//
// Destination cards render inside a Polaris Modal that dismisses on backdrop
// click / ESC, unmounting the card and discarding anything typed but not yet
// saved. We persist in-progress field values in a module-level store — page
// session, MEMORY ONLY (never sessionStorage/localStorage, so secrets are
// never written to disk) — keyed by `${type}:${field}`. Closing + reopening a
// card restores the draft; a successful Save clears it via clearDraftFields.
const draftStore: Record<string, string> = {};

export function useDraftField(type: string, field: string): [string, (v: string) => void] {
  const key = `${type}:${field}`;
  const [value, setValue] = useState(() => draftStore[key] ?? "");
  const set = (v: string) => {
    draftStore[key] = v;
    setValue(v);
  };
  return [value, set];
}

export function clearDraftFields(type: string): void {
  const prefix = `${type}:`;
  for (const k of Object.keys(draftStore)) {
    if (k.startsWith(prefix)) delete draftStore[k];
  }
}

// ---------- Meta CAPI card ----------

// Exported so the Settings → Destinations page can reuse the same form
// for editing already-configured destinations. The optional
// `existingDestination` prop flips the badge from "Optional" to a
// status badge and renders an overwrite warning so the merchant knows
// they're replacing stored credentials, not just adding new ones.
export function MetaCapiDestinationCard({
  shopify,
  onSaved,
  existingDestination,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  onSaved: () => void;
  existingDestination?: DestinationCardExistingView;
}) {
  const [pixelId, setPixelId] = useDraftField("meta_capi", "pixelId");
  const [capiToken, setCapiToken] = useDraftField("meta_capi", "capiToken");
  const [testEventCode, setTestEventCode] = useDraftField("meta_capi", "testEventCode");
  const [state, setState] = useState<CardState>({ kind: "idle" });

  // First-time configuration requires both fields; edit mode requires
  // only one filled (backend partial-update preserves the rest).
  const hasAnyInput =
    pixelId.trim().length > 0 ||
    capiToken.trim().length > 0 ||
    testEventCode.trim().length > 0;
  const canSubmit = existingDestination?.configured
    ? hasAnyInput
    : pixelId.trim().length > 0 && capiToken.trim().length > 0;

  const buildBody = () => ({
    pixelId: pixelId.trim(),
    capiToken: capiToken.trim(),
    // Send testEventCode only if populated; backend schema marks it
    // nullish and rejecting on a present-but-empty-string would be a pain
    // for merchants who fat-finger a delete.
    ...(testEventCode.trim() ? { testEventCode: testEventCode.trim() } : {}),
  });

  const onTest = async () => {
    setState({ kind: "working", what: "test" });
    try {
      const resp = await apiFetch<{ ok: boolean; status: number }>(
        shopify,
        "/api/destinations/meta_capi/test",
        { method: "POST", body: JSON.stringify(buildBody()) },
      );
      setState({
        kind: "success",
        message: `Meta accepted the test event (HTTP ${resp.status}).`,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Test failed: ${msg}` });
    }
  };

  const onSave = async () => {
    setState({ kind: "working", what: "save" });
    try {
      const resp = await apiFetch(shopify, "/api/destinations/meta_capi", {
        method: "POST",
        body: JSON.stringify(buildBody()),
      });
      setState({ kind: "success", message: withReplayNote("Meta CAPI saved.", "meta_capi", resp) });
      clearDraftFields("meta_capi");
      onSaved();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Save failed: ${msg}` });
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Meta Conversions API
          </Text>
          <DestinationStatusBadge existing={existingDestination} />
        </InlineStack>
        <Text as="p" tone="subdued">
          Deduplicated server-side events to your Meta Pixel. Find your pixel
          ID and generate a CAPI token in{" "}
          <Link
            url="https://business.facebook.com/events_manager2/"
            target="_blank"
            removeUnderline
          >
            Meta Events Manager
          </Link>
          {" "}→ Settings.
        </Text>

        <OverwriteWarning existing={existingDestination} />
        <TestCodeNotice
          shopify={shopify}
          type="meta_capi"
          existing={existingDestination}
          onCleared={(message) => {
            if (message) setState({ kind: "success", message });
            onSaved();
          }}
        />

        <TextField
          label="Pixel ID"
          value={pixelId}
          onChange={setPixelId}
          autoComplete="off"
          placeholder="1234567890123456"
        />
        <TextField
          label="CAPI access token"
          value={capiToken}
          onChange={setCapiToken}
          autoComplete="new-password"
          type="password"
          placeholder="EAAG..."
        />
        <TextField
          label="Test event code (optional)"
          value={testEventCode}
          onChange={setTestEventCode}
          autoComplete="off"
          placeholder="TEST12345"
          helpText="Provide a code from Events Manager → Test Events to route test sends into the sandbox instead of production."
        />

        <StatusBanner state={state} />

        <InlineStack gap="200">
          <Button
            onClick={onTest}
            disabled={
              !(existingDestination?.configured || canSubmit) ||
              state.kind === "working"
            }
            loading={state.kind === "working" && state.what === "test"}
          >
            {existingDestination?.configured ? "Send test event" : "Test connection"}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!canSubmit || state.kind === "working"}
            loading={state.kind === "working" && state.what === "save"}
          >
            {existingDestination?.configured ? "Save changes" : "Save"}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// ---------- GA4 card ----------

// Exported alongside MetaCapiDestinationCard for reuse on the
// Settings → Destinations page. See that component for prop semantics.
export function Ga4DestinationCard({
  shopify,
  onSaved,
  existingDestination,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  onSaved: () => void;
  existingDestination?: DestinationCardExistingView;
}) {
  const [measurementId, setMeasurementId] = useDraftField("ga4", "measurementId");
  const [apiSecret, setApiSecret] = useDraftField("ga4", "apiSecret");
  const [state, setState] = useState<CardState>({ kind: "idle" });

  // First-time configuration requires both fields; edit mode requires
  // only one filled (backend partial-update preserves the rest).
  const hasAnyInput =
    measurementId.trim().length > 0 || apiSecret.trim().length > 0;
  const canSubmit = existingDestination?.configured
    ? hasAnyInput
    : measurementId.trim().length > 0 && apiSecret.trim().length > 0;

  const buildBody = () => ({
    measurementId: measurementId.trim(),
    apiSecret: apiSecret.trim(),
  });

  const onTest = async () => {
    setState({ kind: "working", what: "test" });
    try {
      const resp = await apiFetch<{ ok: boolean; status: number }>(
        shopify,
        "/api/destinations/ga4/test",
        { method: "POST", body: JSON.stringify(buildBody()) },
      );
      setState({
        kind: "success",
        message: `GA4 accepted the test event (HTTP ${resp.status}).`,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Test failed: ${msg}` });
    }
  };

  const onSave = async () => {
    setState({ kind: "working", what: "save" });
    try {
      const resp = await apiFetch(shopify, "/api/destinations/ga4", {
        method: "POST",
        body: JSON.stringify(buildBody()),
      });
      setState({ kind: "success", message: withReplayNote("GA4 saved.", "ga4", resp) });
      clearDraftFields("ga4");
      onSaved();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Save failed: ${msg}` });
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Google Analytics 4
          </Text>
          <DestinationStatusBadge existing={existingDestination} />
        </InlineStack>
        <Text as="p" tone="subdued">
          Server-side events sent via GA4 Measurement Protocol. In GA4 admin,
          go to Data Streams → your web stream → Measurement Protocol API
          secrets to mint a secret.
        </Text>

        <OverwriteWarning existing={existingDestination} />

        <TextField
          label="Measurement ID"
          value={measurementId}
          onChange={setMeasurementId}
          autoComplete="off"
          placeholder="G-ABCDEF1234"
        />
        <TextField
          label="API secret"
          value={apiSecret}
          onChange={setApiSecret}
          autoComplete="new-password"
          type="password"
        />

        <StatusBanner state={state} />

        <InlineStack gap="200">
          <Button
            onClick={onTest}
            disabled={
              !(existingDestination?.configured || canSubmit) ||
              state.kind === "working"
            }
            loading={state.kind === "working" && state.what === "test"}
          >
            {existingDestination?.configured ? "Send test event" : "Test connection"}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!canSubmit || state.kind === "working"}
            loading={state.kind === "working" && state.what === "save"}
          >
            {existingDestination?.configured ? "Save changes" : "Save"}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// ---------- Klaviyo card ----------

// Klaviyo destination card. Single field (private API key) — Klaviyo's
// Events API only needs the key, no separate "ID" like Meta's pixelId or
// GA4's measurementId. Pre-built flows on Klaviyo's side hook onto our
// standard metric names ("Placed Order", "Viewed Product", etc.) with
// no extra configuration on the merchant's side.
//
// Email + SMS reach is the biggest reason merchants reach for Klaviyo —
// see competitor research notes for ranking among Littledata/Elevar's
// shared integrations.
export function KlaviyoDestinationCard({
  shopify,
  onSaved,
  existingDestination,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  onSaved: () => void;
  existingDestination?: DestinationCardExistingView;
}) {
  const [apiKey, setApiKey] = useDraftField("klaviyo", "apiKey");
  const [state, setState] = useState<CardState>({ kind: "idle" });

  const canSubmit = apiKey.trim().length > 0;

  const buildBody = () => ({ apiKey: apiKey.trim() });

  const onTest = async () => {
    setState({ kind: "working", what: "test" });
    try {
      const resp = await apiFetch<{ ok: boolean; status: number }>(
        shopify,
        "/api/destinations/klaviyo/test",
        { method: "POST", body: JSON.stringify(buildBody()) },
      );
      setState({
        kind: "success",
        message: `Klaviyo accepted the test event (HTTP ${resp.status}).`,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Test failed: ${msg}` });
    }
  };

  const onSave = async () => {
    setState({ kind: "working", what: "save" });
    try {
      const resp = await apiFetch(shopify, "/api/destinations/klaviyo", {
        method: "POST",
        body: JSON.stringify(buildBody()),
      });
      setState({ kind: "success", message: withReplayNote("Klaviyo saved.", "klaviyo", resp) });
      clearDraftFields("klaviyo");
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Save failed: ${msg}` });
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Klaviyo
          </Text>
          <DestinationStatusBadge existing={existingDestination} />
        </InlineStack>
        <Text as="p" tone="subdued">
          Forward server-side events (Placed Order, Viewed Product, Started
          Checkout, etc.) to your Klaviyo account so flows fire on accurate,
          de-duplicated signals. Generate a private API key in{" "}
          <Link
            url="https://www.klaviyo.com/account#api-keys-tab"
            target="_blank"
            removeUnderline
          >
            Klaviyo → Settings → API Keys
          </Link>
          {" "}→ Create Private API Key. The key must include "Events: Write".
        </Text>

        <OverwriteWarning existing={existingDestination} />

        <TextField
          label="Private API key"
          value={apiKey}
          onChange={setApiKey}
          autoComplete="new-password"
          type="password"
          placeholder="pk_..."
          helpText="Starts with pk_. Browser-side public keys won't work here, because Klaviyo's Events API requires a server-side private key."
        />

        <StatusBanner state={state} />

        <InlineStack gap="200">
          <Button
            onClick={onTest}
            disabled={
              !(existingDestination?.configured || canSubmit) ||
              state.kind === "working"
            }
            loading={state.kind === "working" && state.what === "test"}
          >
            {existingDestination?.configured ? "Send test event" : "Test connection"}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!canSubmit || state.kind === "working"}
            loading={state.kind === "working" && state.what === "save"}
          >
            {existingDestination?.configured ? "Save changes" : "Save"}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// ---------- TikTok card ----------

// TikTok Events API destination. Two fields (pixel ID + access token,
// matching the Meta CAPI shape) since TikTok's Events API needs both an
// asset identifier and an authenticated long-lived token. PII is hashed
// server-side before send — merchants enter normal pixel credentials.
export function TikTokDestinationCard({
  shopify,
  onSaved,
  existingDestination,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  onSaved: () => void;
  existingDestination?: DestinationCardExistingView;
}) {
  const [pixelId, setPixelId] = useDraftField("tiktok", "pixelId");
  const [accessToken, setAccessToken] = useDraftField("tiktok", "accessToken");
  const [testEventCode, setTestEventCode] = useDraftField("tiktok", "testEventCode");
  const [state, setState] = useState<CardState>({ kind: "idle" });

  // First-time configuration requires both required fields; edit mode
  // requires only one filled (backend partial-update preserves the rest).
  const hasAnyInput =
    pixelId.trim().length > 0 ||
    accessToken.trim().length > 0 ||
    testEventCode.trim().length > 0;
  const canSubmit = existingDestination?.configured
    ? hasAnyInput
    : pixelId.trim().length > 0 && accessToken.trim().length > 0;

  const buildBody = () => ({
    pixelId: pixelId.trim(),
    accessToken: accessToken.trim(),
    ...(testEventCode.trim() ? { testEventCode: testEventCode.trim() } : {}),
  });

  const onTest = async () => {
    setState({ kind: "working", what: "test" });
    try {
      const resp = await apiFetch<{ ok: boolean; status: number }>(
        shopify,
        "/api/destinations/tiktok/test",
        { method: "POST", body: JSON.stringify(buildBody()) },
      );
      setState({
        kind: "success",
        message: `TikTok accepted the test event (HTTP ${resp.status}).`,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Test failed: ${msg}` });
    }
  };

  const onSave = async () => {
    setState({ kind: "working", what: "save" });
    try {
      const resp = await apiFetch(shopify, "/api/destinations/tiktok", {
        method: "POST",
        body: JSON.stringify(buildBody()),
      });
      setState({ kind: "success", message: withReplayNote("TikTok saved.", "tiktok", resp) });
      clearDraftFields("tiktok");
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Save failed: ${msg}` });
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            TikTok Events API
          </Text>
          <DestinationStatusBadge existing={existingDestination} />
        </InlineStack>
        <Text as="p" tone="subdued">
          Server-side conversion events to your TikTok pixel. Find your pixel
          code and generate an access token in{" "}
          <Link
            url="https://ads.tiktok.com/i18n/events_manager"
            target="_blank"
            removeUnderline
          >
            TikTok Ads Manager
          </Link>
          {" "}→ Assets → Events → Web Events → Manage.
        </Text>

        <OverwriteWarning existing={existingDestination} />
        <TestCodeNotice
          shopify={shopify}
          type="tiktok"
          existing={existingDestination}
          onCleared={(message) => {
            if (message) setState({ kind: "success", message });
            onSaved();
          }}
        />

        <TextField
          label="Pixel code"
          value={pixelId}
          onChange={setPixelId}
          autoComplete="off"
          placeholder="C8XXXXXXXXXXXXX"
        />
        <TextField
          label="Access token"
          value={accessToken}
          onChange={setAccessToken}
          autoComplete="new-password"
          type="password"
        />
        <TextField
          label="Test event code (optional)"
          value={testEventCode}
          onChange={setTestEventCode}
          autoComplete="off"
          placeholder="TEST12345"
          helpText={
            existingDestination?.configured
              ? "Routes test sends into TikTok's Test Events view. Paste a code from TikTok, leave the fields above blank, and click Test connection. Your saved pixel code + access token are reused."
              : "Routes test sends into TikTok's Test Events view rather than production reporting."
          }
        />

        <StatusBanner state={state} />

        <InlineStack gap="200">
          <Button
            onClick={onTest}
            disabled={
              !(existingDestination?.configured || canSubmit) ||
              state.kind === "working"
            }
            loading={state.kind === "working" && state.what === "test"}
          >
            {existingDestination?.configured ? "Send test event" : "Test connection"}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!canSubmit || state.kind === "working"}
            loading={state.kind === "working" && state.what === "save"}
          >
            {existingDestination?.configured ? "Save changes" : "Save"}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// ---------- Google Ads card ----------

// Google Ads Enhanced Conversions for Web. Two-step flow:
//
//   1. Merchant clicks "Connect Google Ads". A popup opens on Datacrow's
//      begin page, which sets a short-lived cookie tying the flow to this
//      browser (review W16) and hands off to Google's consent screen. On
//      success, the grantId reaches this component (poll, broadcast or
//      postMessage) and the popup closes itself.
//   2. Merchant fills in Customer ID + Conversion action ID (the two
//      Google-side identifiers we can't derive from OAuth) and clicks
//      Save. The grantId travels with the save request as
//      `_oauthGrantId`; the backend pairs it with the form fields to
//      build the final credentials record.
//
// The refresh token never transits through the browser — it's stashed
// in Redis by the OAuth callback and pulled out server-side at Save
// time. See src/routes/google-ads-oauth.ts for the full picture.
export function GoogleAdsDestinationCard({
  shopify,
  onSaved,
  existingDestination,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  onSaved: () => void;
  existingDestination?: DestinationCardExistingView;
}) {
  const [customerId, setCustomerId] = useDraftField("google_ads", "customerId");
  const [conversionActionId, setConversionActionId] = useDraftField("google_ads", "conversionActionId");
  const [loginCustomerId, setLoginCustomerId] = useDraftField("google_ads", "loginCustomerId");
  // grantId is populated by the OAuth popup's postMessage. Null until
  // the merchant clicks Connect AND the popup completes successfully.
  // Held only in component state — never localStorage, never sent
  // anywhere except back to our backend's Save handler.
  const [grantId, setGrantId] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [state, setState] = useState<CardState>({ kind: "idle" });

  const isConnected = grantId !== null;
  // First-time: requires OAuth completion + both ID fields. Edit mode: a
  // fresh sign-in, alone or with changed IDs. The ID fields are disabled
  // without one, so IDs still typed once the grant is gone (a failed save
  // drops it) are stale drafts: saved on the stored sign-in, they would
  // report "saved" over a revoked one (surface-1). The backend's
  // partial-update keeps every blank field (the IDs, and the refresh token
  // when no grant comes with the save), so a reconnect after a revoked
  // sign-in is Connect Google Ads, then Save changes, with the IDs left as
  // they are; that save re-sends the conversions that failed (the
  // destination-down email's instructions).
  const canSubmit = existingDestination?.configured
    ? isConnected
    : isConnected &&
      customerId.trim().length > 0 &&
      conversionActionId.trim().length > 0;

  const buildBody = () => ({
    customerId: customerId.trim(),
    conversionActionId: conversionActionId.trim(),
    _oauthGrantId: grantId,
    ...(loginCustomerId.trim() ? { loginCustomerId: loginCustomerId.trim() } : {}),
  });

  const onConnect = async () => {
    setConnecting(true);
    setConnectError(null);

    // Open the popup now, synchronously inside the click. Opened after the
    // /start await below, Safari and strict blockers treat it as
    // unsolicited and block it. It shows about:blank until /start answers.
    // Keep the opener (no "noopener"): window.open would return null and
    // the close watcher below needs the reference.
    const popup = window.open("about:blank", "google-ads-oauth", "width=600,height=720");
    if (!popup) {
      setConnecting(false);
      setConnectError(
        "Your browser blocked the sign-in window. Allow popups for this site, then click Connect Google Ads again.",
      );
      return;
    }

    try {
      // The server generates a state token, stashes our storeId in Redis
      // under it, and returns beginUrl: Datacrow's begin page, which binds
      // this browser with a cookie and 302s to Google (review W16). pollKey
      // is the same state token, returned so we can ask /poll whether the
      // popup has finished. Server-mediated polling is the primary bridge
      // because browser storage partitioning (Firefox Total Cookie
      // Protection, Chrome's site isolation) breaks every client-side
      // popup-to-parent bridge when the parent is embedded in another
      // origin's frame; see the OAuth route's polling comment.
      const resp = await apiFetch<{ beginUrl?: string; pollKey: string }>(
        shopify,
        "/api/destinations/google_ads/oauth/start",
      );
      if (!resp.beginUrl) {
        // An older server answered (deploy skew). Its consent URL would
        // skip the begin page, and the callback would refuse it anyway.
        popup.close();
        setConnecting(false);
        setConnectError("Datacrow was updated. Reload the page, then click Connect Google Ads again.");
        return;
      }
      popup.location.href = resp.beginUrl;

      // Bridge from the OAuth popup back to here. Two channels both
      // live for the duration of the flow; the first one to fire
      // wins (the other is ignored via the `done` flag).
      //
      // PRIMARY: BroadcastChannel. Works between same-origin documents
      // regardless of opener relationship. This matters because
      // Google's consent page has a strict COOP that severs
      // window.opener once the popup visits it — see the
      // src/routes/google-ads-oauth.ts comment for the full story.
      //
      // FALLBACK: window message listener. Free to keep — if a future
      // browser change lets opener survive the cross-origin hop, this
      // path still works.
      let done = false;
      type BridgePayload = { type?: string; ok?: boolean; grantId?: string; error?: string };
      const handlePayload = (data: BridgePayload, source: "broadcast" | "postmessage" | "poll") => {
        if (done) return;
        if (!data || data.type !== "google-ads-oauth") return;
        done = true;

        console.log("[google-ads oauth] bridge received", {
          source,
          ok: data.ok,
          hasGrantId: !!data.grantId,
        });

        window.removeEventListener("message", onMessage);
        channel.close();
        setConnecting(false);

        if (data.ok && data.grantId) {
          setGrantId(data.grantId);
          setConnectError(null);
        } else {
          setConnectError(data.error ?? "Connection failed");
        }
      };

      const channel = new BroadcastChannel("google-ads-oauth-bridge");
      channel.onmessage = (event) => handlePayload(event.data as BridgePayload, "broadcast");

      // Only our own callback page may answer (review S10); it is served
      // from this app's origin.
      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        handlePayload(event.data as BridgePayload, "postmessage");
      };
      window.addEventListener("message", onMessage);

      // Server-mediated polling. Beats both BroadcastChannel and
      // window.opener.postMessage when browser storage partitioning
      // is in play. Runs in parallel; whichever channel lands the
      // payload first wins (handlePayload's `done` flag suppresses
      // the others). Stops after ~5 min to avoid hammering the
      // backend if the merchant abandoned the flow.
      const pollDeadline = Date.now() + 5 * 60 * 1000;
      const pollTimer = setInterval(async () => {
        if (done || Date.now() > pollDeadline) {
          clearInterval(pollTimer);
          return;
        }
        try {
          const r = await apiFetch<
            | { status: "pending" }
            | { status: "complete"; grantId: string }
            | { status: "error"; error: string }
          >(shopify, `/api/destinations/google_ads/oauth/poll?pollKey=${encodeURIComponent(resp.pollKey)}`);
          if (r.status === "complete") {
            clearInterval(pollTimer);
            handlePayload(
              { type: "google-ads-oauth", ok: true, grantId: r.grantId },
              "poll",
            );
          } else if (r.status === "error") {
            clearInterval(pollTimer);
            handlePayload(
              { type: "google-ads-oauth", ok: false, error: r.error },
              "poll",
            );
          }
          // status === "pending" → keep polling
        } catch {
          // Transient errors (network blips, server restart) are
          // expected during a 5-min window. Don't abort polling on
          // a single failure — let the next tick try again.
        }
      }, 2000);

      // Defensive: if the popup is closed without completing OAuth,
      // un-stick the loading state after a few seconds.
      //
      // Generous 2-second grace period after popup.closed because the
      // browser's postMessage delivery can be delayed when the parent
      // is busy (React re-renders, network in flight). Better to
      // briefly leave the spinner up than to flash a false "closed
      // before completing" error when the grant DID land.
      const closeWatcher = setInterval(() => {
        if (popup.closed) {
          clearInterval(closeWatcher);
          // Generous grace period — give the poller two more ticks
          // (4s) to land the result before declaring failure. The
          // popup writes the result entry BEFORE closing itself, so
          // it's racing the next poll tick on a healthy backend.
          setTimeout(() => {
            setConnecting((isStillConnecting) => {
              if (isStillConnecting && !done) {
                window.removeEventListener("message", onMessage);
                channel.close();
                clearInterval(pollTimer);
                // A cookie mismatch at the callback writes no result for
                // /poll (review W16). Where the popup's own message can't
                // reach this frame, this is all the merchant sees, so it
                // names the likely cause.
                setConnectError(
                  `The Google window closed before the connection finished. Click Connect Google Ads to retry. If it keeps failing, allow cookies for ${window.location.host}. The sign-in window needs one to finish.`,
                );
              }
              return false;
            });
          }, 4000);
        }
      }, 500);
    } catch (err) {
      // /start failed: nothing will ever load in the blank popup.
      popup.close();
      setConnecting(false);
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setConnectError(`Couldn't start the connection: ${msg}`);
    }
  };

  const onTest = async () => {
    setState({ kind: "working", what: "test" });
    try {
      // The form only with a grant. Without one (a configured card: Test
      // connection needs a grant) the ID fields are disabled, so IDs still
      // typed are stale drafts a failed save left behind; posting them would
      // test IDs that aren't saved. An empty body tests exactly the saved
      // credentials (ui-oauth-1).
      const resp = await apiFetch<{ ok: boolean; status: number }>(
        shopify,
        "/api/destinations/google_ads/test",
        { method: "POST", body: JSON.stringify(isConnected ? buildBody() : {}) },
      );
      setState({
        kind: "success",
        message: `Google Ads accepted the test conversion (HTTP ${resp.status}).`,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Test failed: ${msg}` });
      // The grant expired (30 minutes) or was spent: bring Connect Google
      // Ads back, as the message asks. The typed IDs stay (draft fields).
      if (err instanceof ApiError && err.status === 400 && /grant expired/i.test(err.message)) setGrantId(null);
    }
  };

  const onSave = async () => {
    setState({ kind: "working", what: "save" });
    try {
      const resp = await apiFetch(shopify, "/api/destinations/google_ads", {
        method: "POST",
        body: JSON.stringify(buildBody()),
      });
      setState({ kind: "success", message: withReplayNote("Google Ads saved.", "google_ads", resp) });
      clearDraftFields("google_ads");
      // After successful save, the grant is consumed server-side.
      // Clear it locally so a follow-up edit triggers re-Connect.
      setGrantId(null);
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Save failed: ${msg}` });
      // The route consumes the grant before it validates anything, so after
      // a failed save it is gone (or had expired): never offer it again.
      // Connect Google Ads comes back; the typed IDs stay (draft fields).
      setGrantId(null);
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Google Ads
          </Text>
          <DestinationStatusBadge existing={existingDestination} />
        </InlineStack>
        <Text as="p" tone="subdued">
          Send server-side purchase conversions to Google Ads via Enhanced
          Conversions for Web. Recovers attribution lost to cookie blocking
          and iOS privacy changes.
        </Text>

        <OverwriteWarning existing={existingDestination} />

        {!isConnected ? (
          <BlockStack gap="200">
            <Banner tone="info">
              <p>
                Click the button below to authorize Datacrow to upload
                conversions to your Google Ads account. You'll be sent to
                Google's consent screen in a new window.
              </p>
            </Banner>
            <InlineStack>
              <Button
                variant="primary"
                onClick={onConnect}
                loading={connecting}
                disabled={connecting}
              >
                Connect Google Ads
              </Button>
            </InlineStack>
            {connectError && (
              <Banner tone="critical">
                <p>{connectError}</p>
              </Banner>
            )}
          </BlockStack>
        ) : (
          <Banner tone="success">
            {existingDestination?.configured ? (
              <p>
                <strong>Connected.</strong> Click Save changes to use this
                sign-in. Leave the IDs blank to keep the saved ones.
              </p>
            ) : (
              <p>
                <strong>Connected.</strong> Fill in your Customer ID and
                Conversion action ID below, then click Save.
              </p>
            )}
          </Banner>
        )}

        <TextField
          label="Customer ID"
          value={customerId}
          onChange={setCustomerId}
          autoComplete="off"
          placeholder="123-456-7890 (dashes optional)"
          helpText="Your Google Ads account ID. Find it top-right in the Ads UI."
          disabled={!isConnected}
        />
        <TextField
          label="Conversion action ID"
          value={conversionActionId}
          onChange={setConversionActionId}
          autoComplete="off"
          placeholder="1234567890"
          helpText="In Google Ads: Goals → Conversions → All conversions → click your purchase action → copy its Conversion type ID. The action's source must be Website (Import from clicks)."
          disabled={!isConnected}
        />
        <TextField
          label="Login customer ID (optional)"
          value={loginCustomerId}
          onChange={setLoginCustomerId}
          autoComplete="off"
          placeholder="Manager (MCC) account ID, only if your account is managed"
          helpText="Required if an agency / manager account oversees your Ads account."
          disabled={!isConnected}
        />

        <StatusBanner state={state} />

        <InlineStack gap="200">
          <Button
            onClick={onTest}
            disabled={
              !(existingDestination?.configured || canSubmit) ||
              state.kind === "working"
            }
            loading={state.kind === "working" && state.what === "test"}
          >
            {existingDestination?.configured ? "Send test event" : "Test connection"}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!canSubmit || state.kind === "working"}
            loading={state.kind === "working" && state.what === "save"}
          >
            {existingDestination?.configured ? "Save changes" : "Save"}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// ---------- Pinterest card ----------

// Pinterest Conversions API destination. Two fields (ad account ID +
// access token) — Pinterest's auth is a long-lived bearer token, no
// OAuth complexity like Google Ads. Strong fit for fashion / home /
// lifestyle Shopify stores where Pinterest skews purchase-intent
// visual discovery.
export function PinterestDestinationCard({
  shopify,
  onSaved,
  existingDestination,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  onSaved: () => void;
  existingDestination?: DestinationCardExistingView;
}) {
  const [adAccountId, setAdAccountId] = useDraftField("pinterest", "adAccountId");
  const [accessToken, setAccessToken] = useDraftField("pinterest", "accessToken");
  const [state, setState] = useState<CardState>({ kind: "idle" });

  // Overlap warnings (Pinterest's own Shopify app installed → double-count)
  // are now rendered generically by <DestinationOverlapWarning> in the hub
  // modal + onboarding, driven by storeContext.overlappingDestinations —
  // not per-card. See DESTINATION_OVERLAP_WARNING below.

  // First-time configuration requires both fields; edit mode requires
  // only one filled (backend partial-update preserves the rest).
  const hasAnyInput =
    adAccountId.trim().length > 0 || accessToken.trim().length > 0;
  const canSubmit = existingDestination?.configured
    ? hasAnyInput
    : adAccountId.trim().length > 0 && accessToken.trim().length > 0;

  const buildBody = () => ({
    adAccountId: adAccountId.trim(),
    accessToken: accessToken.trim(),
  });

  const onTest = async () => {
    setState({ kind: "working", what: "test" });
    try {
      const resp = await apiFetch<{ ok: boolean; status: number }>(
        shopify,
        "/api/destinations/pinterest/test",
        { method: "POST", body: JSON.stringify(buildBody()) },
      );
      setState({
        kind: "success",
        message: `Pinterest accepted the test event (HTTP ${resp.status}).`,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Test failed: ${msg}` });
    }
  };

  const onSave = async () => {
    setState({ kind: "working", what: "save" });
    try {
      const resp = await apiFetch(shopify, "/api/destinations/pinterest", {
        method: "POST",
        body: JSON.stringify(buildBody()),
      });
      setState({ kind: "success", message: withReplayNote("Pinterest saved.", "pinterest", resp) });
      clearDraftFields("pinterest");
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setState({ kind: "error", message: `Save failed: ${msg}` });
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Pinterest Conversions API
          </Text>
          <DestinationStatusBadge existing={existingDestination} />
        </InlineStack>
        <Text as="p" tone="subdued">
          Server-side conversion events to Pinterest so ad attribution
          survives cookie blocking + iOS privacy changes. Find your ad
          account ID and generate an access token in{" "}
          <Link
            url="https://ads.pinterest.com/"
            target="_blank"
            removeUnderline
          >
            Pinterest Business Manager
          </Link>
          {" "}→ Ads → Conversions → Generate access token (requires the
          Conversions API scope).
        </Text>

        <OverwriteWarning existing={existingDestination} />

        <TextField
          label="Ad account ID"
          value={adAccountId}
          onChange={setAdAccountId}
          autoComplete="off"
          placeholder="549755813999"
        />
        <TextField
          label="Access token"
          value={accessToken}
          onChange={setAccessToken}
          autoComplete="new-password"
          type="password"
          helpText="Long-lived bearer token. Pinterest tokens typically last ~1 year before requiring rotation."
        />
        <Text as="p" tone="subdued" variant="bodySm">
          To test, click <strong>Test connection</strong> below. Datacrow sends a
          test event to Pinterest&apos;s <strong>Test events</strong> view automatically
          (no test code needed; Pinterest uses a <code>?test=true</code> request).
        </Text>

        <StatusBanner state={state} />

        <InlineStack gap="200">
          <Button
            onClick={onTest}
            disabled={
              !(existingDestination?.configured || canSubmit) ||
              state.kind === "working"
            }
            loading={state.kind === "working" && state.what === "test"}
          >
            {existingDestination?.configured ? "Send test event" : "Test connection"}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!canSubmit || state.kind === "working"}
            loading={state.kind === "working" && state.what === "save"}
          >
            {existingDestination?.configured ? "Save changes" : "Save"}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// ---------- Shared destination-status badge + overwrite warning ----------

export function DestinationStatusBadge({
  existing,
}: {
  existing: DestinationCardExistingView | undefined;
}) {
  // Three states, distinct tones so a scan of the page tells the merchant
  // which destinations need attention:
  //   • Not configured yet → "Optional" (info tone — neutral nudge)
  //   • Configured + enabled → "Active" (success — events flowing)
  //   • Configured + disabled → "Disabled" (warning — was set up, now paused)
  if (!existing?.configured) {
    return <Badge tone="info">Optional</Badge>;
  }
  if (existing.enabled) {
    return <Badge tone="success">Active</Badge>;
  }
  return <Badge tone="warning">Disabled</Badge>;
}

export function OverwriteWarning({
  existing,
}: {
  existing: DestinationCardExistingView | undefined;
}) {
  // Stored credentials are encrypted at rest and the backend never echoes
  // them back to the browser — so the form can't pre-fill with current
  // values. Instead, the backend sends display-safe HINTS (last 4 chars
  // of public IDs, "set" for secrets) and we render them here so the
  // merchant knows what's stored without us exposing the actual values.
  //
  // The corresponding backend behavior is partial-update: leaving a field
  // blank preserves whatever's currently stored. Only non-empty fields
  // get overwritten. This lets a merchant rotate just one credential
  // (e.g. a CAPI token) without re-entering the others.
  if (!existing?.configured) return null;

  const hints = existing.hints ?? {};
  const hintLines = Object.entries(hints).filter(([, v]) => v && v.length > 0);

  return (
    <Banner tone="info" title="This destination is already configured">
      <BlockStack gap="200">
        <p>
          Leave fields blank to <strong>keep their current values</strong>.
          Only fields you fill in are updated. To re-test without re-entering
          anything, just click <strong>Send test event</strong> (optionally
          paste a fresh test event code first) and your saved credentials are
          reused.
        </p>
        {hintLines.length > 0 && (
          <BlockStack gap="100">
            <Text as="p" tone="subdued" variant="bodySm">Currently stored:</Text>
            <BlockStack gap="050">
              {hintLines.map(([key, value]) => (
                <Text key={key} as="p" tone="subdued" variant="bodySm">
                  <strong>{prettyFieldName(key)}:</strong>{" "}
                  {value === "set" ? <em>set (hidden)</em> : <code>{value}</code>}
                </Text>
              ))}
            </BlockStack>
          </BlockStack>
        )}
      </BlockStack>
    </Banner>
  );
}

// A saved test event code rides on every live Meta or TikTok event, and a
// blank form field means "keep the stored value", so there was no way to
// remove one from the app (review W15). This shows the code and clears it
// with an explicit null, which the backend stores as unset. Nothing is
// replayed while a code is saved, so clearing it can start the replay an
// earlier credentials save skipped (critic-1 P7); onCleared gets the
// sentence for the card's banner then, since this notice is gone once the
// destination reloads without the code.
// Exported and widened to DestinationType: Reddit stores a test-event ID the
// same way Meta and TikTok store a test event code, and the hardcoded union
// meant it would never have shown this warning. The notice renders nothing
// unless a code is actually saved, so widening it cannot make it appear
// where it should not.
export function TestCodeNotice({
  shopify,
  type,
  existing,
  onCleared,
}: {
  shopify: ReturnType<typeof useAppBridge>;
  type: DestinationType;
  existing: DestinationCardExistingView | undefined;
  onCleared: (message: string | null) => void;
}) {
  const [state, setState] = useState<"idle" | "working" | "error">("idle");
  const code = existing?.configured ? existing.hints?.testEventCode : undefined;
  if (!code) return null;

  const onClear = async () => {
    setState("working");
    try {
      const resp = await apiFetch(shopify, `/api/destinations/${type}`, {
        method: "POST",
        body: JSON.stringify({ testEventCode: null }),
      });
      setState("idle");
      const note = replayNote(type, resp);
      onCleared(note ? `Test code removed. ${note}` : null);
    } catch {
      setState("error");
    }
  };

  return (
    <Banner tone="warning" title="A test event code is saved">
      <BlockStack gap="200">
        <p>
          Every live event goes out with test code <code>{code}</code>. Remove it once you're
          done testing.
        </p>
        {state === "error" && <p>Couldn't remove the test code. Try again.</p>}
        <InlineStack>
          <Button onClick={onClear} loading={state === "working"}>
            Remove test code
          </Button>
        </InlineStack>
      </BlockStack>
    </Banner>
  );
}

// Map credential schema field names ("pixelId", "capiToken", etc.) to
// labels merchants recognize from the platform UIs. Keeping this in one
// place means a future field-name change in the schema only requires one
// label update.
function prettyFieldName(key: string): string {
  const map: Record<string, string> = {
    pixelId: "Pixel ID",
    capiToken: "CAPI token",
    testEventCode: "Test event code",
    measurementId: "Measurement ID",
    apiSecret: "API secret",
    apiKey: "API key",
    accessToken: "Access token",
    customerId: "Customer ID",
    conversionActionId: "Conversion action ID",
    refreshToken: "Refresh token",
    clientId: "OAuth client ID",
    clientSecret: "OAuth client secret",
    loginCustomerId: "Login (MCC) customer ID",
    adAccountId: "Ad account ID",
  };
  return map[key] ?? key;
}

// ---------- Shared per-card status banner ----------

export function StatusBanner({ state }: { state: CardState }) {
  if (state.kind === "success") {
    return (
      <Banner tone="success">
        <p>{state.message}</p>
      </Banner>
    );
  }
  if (state.kind === "error") {
    return (
      <Banner tone="critical">
        <p>{state.message}</p>
      </Banner>
    );
  }
  return null;
}
